import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  TextField,
  Pagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/PersonAddAlt1";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch } from "react-redux";
import { listCustomers } from "../../user/api/actions/query";
import { createAdminUser, updateAdminStatus, updateAdminUser } from "../api/mutation";
import { fetchRoles } from "../api/query";
import { createRole, updateRole, deleteRole } from "../api/mutation";
import RolesView from "../index";
import EditRoles from "../EditRoles";
import menuItems from "../menuItems.json";
import { checkPermission } from "../../common/Sidebar/checkPermission";
import { HIDE_DOMAINS } from "../../../setup/config/env";
import { messageShow } from "../../common/api/actions";
import BrandedLoading from "../../common/BrandedLoading";

const ManageUsers = () => {
  const dispatch = useDispatch();
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    role: "",
    isVerified: false,
    isPublished: false,
    sendEmail: false,
  });
  const [copied, setCopied] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);

  const [view, setView] = useState("users");
  const [selectedRole, setSelectedRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState({
    canView: false,
    canEdit: false,
    canDelete: false,
    canCreate: false,
  });
  const [ rolesPermissions, setRolesPermissions ] = useState({
    canView: false,
    canEdit: false,
    canDelete: false,
    canCreate: false
  })
  const userRole = localStorage.getItem('user');
  const userData = JSON.parse(userRole) || {}
  const isSuperAdmin = userData?.roleDetails?.name === "Super Admin";

  useEffect(() => {
    const fetchAllRoles = async () => {
      try {
        const result = await fetchRoles();
        setRoles(result);
      } catch (error) {
        setRoles([]);
      }
    };
    const fetchUsers = async () => {
      try {
        const { data } = await dispatch(
          listCustomers({
            page,
            filter: {},
            limit: rowsPerPage,
            type: "assignRole",
            role: "admin",
          })
        );
        const filteredList = applyDomainFilter(data.data.list);
        setUsers(filteredList);
        setTotalUsers(isSuperAdmin ? data.data.count : filteredList.length);
      } catch (error) {}
    };
    const loadPermissions = async () => {
      const teamsPerms = await checkPermission("utilities", "teams.teams");
      const rolesPerms = await checkPermission("utilities", "teams.rolesAndPermissions");
      setUserPermissions(teamsPerms);
      setRolesPermissions(rolesPerms);
    };

    const loadAll = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchAllRoles(), fetchUsers(), loadPermissions()]);
      } catch (err) {
        console.error("Error loading team data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadAll();
  }, [dispatch, page, rowsPerPage]);

  const handleCreateRole = async (roleData) => {
    try {
      const res = await createRole(roleData);
      if (res.success) {
        setRoles((prev) => [...prev, res.data]);
        return { success: true };
      } else {
        return { success: false, message: res.message };
      }
    } catch (error) {
      return { success: false, message: "Error creating role" };
    }
  };

  const handleUpdateRole = async (id, roleData) => {
    try {
      const res = await updateRole(id, roleData);
      if (res.success) {
        setRoles((prev) =>
          prev.map((r) => (r._id === id ? { ...r, ...roleData } : r))
        );
        return { success: true };
      } else {
        return { success: false, message: res.message };
      }
    } catch (error) {
      return { success: false, message: "Error updating role" };
    }
  };

  const handleDeleteRole = async (id) => {
    try {
      const res = await deleteRole(id);
      if (res.success) {
        setRoles((prev) => prev.filter((r) => r._id !== id));
        return { success: true };
      } else {
        return { success: false, message: res.message };
      }
    } catch (error) {
      return { success: false, message: "Error deleting role" };
    }
  };

  const handleManagePermissions = (role) => {
    setSelectedRole(role);
    setView("permissions");
  };

  const hiddenDomains = (() => {
    try {
      return JSON.parse(HIDE_DOMAINS || "[]")
        .map((d) => d.trim().toLowerCase());
    } catch (e) {
      return [];
    }
  })();
  const applyDomainFilter = (list) => {
    if (isSuperAdmin) return list;
    return list.filter((user) => {
      const userDomain = user.email?.split("@")[1]?.toLowerCase();
      return !hiddenDomains.includes(userDomain);
    });
  };
  const updateListWithFilter = (updatedList) => {
    setUsers(applyDomainFilter(updatedList));
    setTotalUsers(isSuperAdmin ? updatedList.length : applyDomainFilter(updatedList).length);
  };

  const handleOpenDialog = (type, user = null) => {
    setDialogType(type);
    setSelectedUser(user);
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        mobile: user.mobile || "",
        password: "",
        role: user.roleAssigned || "",
        isVerified: user.isVerified || false,
        isPublished: user.isPublished || false,
        sendEmail: false,
      });
    } else {
      setFormData({ name: "", email: "", mobile: "", password: "", role: "", isPublished: false, isVerified: false, sendEmail: false });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
      if (!formData.name || !formData.email || !formData.role || !formData.mobile) {
        alert("Name, Email, Mobile and Role are required.");
        return;
      }
      let password = formData.password;
      if (!password) password = Math.random().toString(36).slice(-10);
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        mobile: formData.mobile.trim(),
        password,
        roleAssigned: formData.role,
        isVerified: formData.isVerified,
        isPublished: formData.isPublished,
        sendEmail: formData.sendEmail,
      };
    try {
      let result;
      if (dialogType === "create") {
        result = await createAdminUser(payload);
      } else if (dialogType === "edit") {
        result = await updateAdminUser(selectedUser._id, payload);
      }
      if (result.success) {
        setOpenDialog(false);

        if (dialogType === "create") {
          await reloadUsers();
          dispatch(messageShow(`Admin account created successfully for ${formData.name}`));
        } else if (dialogType === "edit") {
          await reloadUsers();
          dispatch(messageShow(`Admin account updated successfully for ${formData.name}`));
        }
      }
      else {
        alert(result.message || "Failed to update admin.");
      }
    } catch (error) {}
  };

  const handleVerifyAdmin = async (user) => {
    if (!window.confirm(`Verify admin "${user.name}"?`)) {
      return;
    }

    try {
      await updateAdminStatus(user._id, "verify");
      dispatch(messageShow(`Admin "${user.name}" has been verified successfully.`));
      await reloadUsers();
    } catch (error) {
      console.error("Failed to verify admin:", error);
      alert("Failed to verify admin. Please try again.");
    }
  };

  const handlePublishAdmin = async (user) => {
    if (!window.confirm(`Approve "${user.name}"?`)) {
      return;
    }

    try {
      await updateAdminStatus(user._id, "publish");
      dispatch(messageShow(`Admin "${user.name}" has been approved and published successfully.`));
      await reloadUsers();
    } catch (error) {
      console.error("Failed to approve admin:", error);
      alert("Failed to approve admin. Please try again.");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  if (view === "permissions" && selectedRole) {
    return (
      <EditRoles
        role={selectedRole}
        onBack={() => {
          setSelectedRole(null);
          setView("roles");
        }}
        onSave={async (updatedRole) => {
          const res = await handleUpdateRole(updatedRole._id, updatedRole);
          if (res.success) {
            setSelectedRole(null);
            setView("roles");
          } else {
            alert(res.message);
          }
        }}
        menuItems={menuItems}
        disabledRoles={["Super Admin", "Store Admin"]}
      />
    );
  }

  if (view === "roles") {
    return (
      <RolesView
        roles={roles}
        permissions={userPermissions}
        onAddRole={handleCreateRole}
        onEditRole={handleUpdateRole}
        onDeleteRole={handleDeleteRole}
        onManagePermissions={handleManagePermissions}
        onBack={() => setView("users")}
      />
    );
  }
  const reloadUsers = async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatch(
        listCustomers({
          page,
          limit: rowsPerPage,
          type: "assignRole",
          role: "admin",
        })
      );
      const filteredList = applyDomainFilter(data.data.list);
      setUsers(filteredList);
      setTotalUsers(isSuperAdmin ? data.data.count : filteredList.length);
    } catch (err) {
      console.error("Failed to reload users", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "grey.50", minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        { rolesPermissions.canView &&
        <Button
          variant="outlined"
          onClick={() => setView("roles")}
          sx={{
            borderRadius: 2,
            px: 3,
            fontWeight: 600,
            borderColor: "#0d3559",
            color: "#0d3559",
            boxShadow: 2,
            "&:hover": {
              borderColor: "#0a2a45",
              backgroundColor: "rgba(13,53,89,0.04)",
            },
          }}
        >
          Roles
        </Button>
        }
        { userPermissions.canCreate && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog("create")}
          sx={{
            borderRadius: 2,
            px: 3,
            fontWeight: 600,
            backgroundColor: "#0d3559",
            boxShadow: 2,
            "&:hover": { backgroundColor: "#0a2a45" },
          }}
        >
          Create Admin
        </Button>)}
      </Box>
      {/* Table Card */}
      <Card sx={{ borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Manage Team
          </Typography>

          {isLoading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <BrandedLoading />
            </Box>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #eee",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      {(userPermissions.canEdit) && (
                      <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {users.map((user) => (
                      <TableRow
                        key={user._id}
                        hover
                        sx={{
                          "&:hover": { bgcolor: "grey.50" },
                          transition: "0.2s",
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.mobile}</TableCell>
                        <TableCell>
                          {roles?.find(
                            (role) => role._id.toString() === user.roleAssigned
                          )?.name || "No Role"}
                        </TableCell>
                        <TableCell>
                          {user.isDeleted ? (
                            <Chip
                              label="Deleted"
                              color="error"
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          ) : user.isPublished && user.isVerified ? (
                            <Chip
                              label="Active"
                              color="success"
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          ) : !user.isPublished && !user.isVerified ? (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Chip
                                label="Pending Approval"
                                color="warning"
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                              <Chip
                                label="Not Verified"
                                color="error"
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            </Box>
                          ) : !user.isPublished ? (
                            <Chip
                              label="Pending Approval"
                              color="warning"
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          ) : user.isPublished && !user.isVerified ? (
                            <Chip
                              label="Not Verified"
                              color="error"
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          ) : (
                            <Chip
                              label="Unknown"
                              color="default"
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          )}
                        </TableCell>
                        {(userPermissions.canEdit || userPermissions.canDelete || userPermissions.canCreate) && (
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {userPermissions.canEdit && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenDialog("edit", user)}
                              sx={{
                                borderRadius: 2,
                                px: 3,
                                fontWeight: 600,
                                textTransform: "none",
                                borderColor: "#0d3559",
                                color: "#0d3559",
                                "&:hover": {
                                  borderColor: "#0a2a45",
                                  backgroundColor: "rgba(13, 53, 89, 0.04)",
                                },
                                boxShadow: 2,
                              }}
                            >
                              Modify
                            </Button>)}
                            {userPermissions.canEdit && !user.isVerified && (
                              <Button
                                variant="contained"
                                size="small"
                                color="info"
                                onClick={() => handleVerifyAdmin(user)}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 2,
                                  fontWeight: 500,
                                  backgroundColor: "#0d3559",
                                  "&:hover": { backgroundColor: "#0a2a45" },
                                }}
                              >
                                Verify
                              </Button>
                            )}

                            {userPermissions.canEdit && !user.isPublished && (
                              <Button
                                variant="contained"
                                size="small"
                                color="warning"
                                onClick={() => handlePublishAdmin(user)}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 2,
                                  fontWeight: 500,
                                }}
                              >
                                Approve
                              </Button>
                            )}
                          </Box>
                        </TableCell>)}
                      </TableRow>
                    ))}

                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No admins found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={Math.ceil(totalUsers / rowsPerPage)}
                  page={page}
                  onChange={handleChangePage}
                  color="primary"
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.1rem",
            px: 2.5,
            py: 2.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          {dialogType === "create" && "Create New Admin"}
          {dialogType === "edit" && `Modify Admin: ${selectedUser?.name}`}
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 3, bgcolor: "background.paper" }}>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              marginTop: 1,
            }}
          >
            {/* Form fields */}
            <TextField
              fullWidth
              label="Name"
              variant="outlined"
              size="medium"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              size="medium"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Mobile Number"
              variant="outlined"
              size="medium"
              value={formData.mobile}
              onChange={(e) =>
                setFormData({ ...formData, mobile: e.target.value })
              }
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                label="Password"
                type="text"
                variant="outlined"
                size="medium"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
              />
              <Button
                variant="outlined"
                onClick={() => {
                  const newPass = Math.random().toString(36).slice(-10);
                  setFormData({ ...formData, password: newPass });
                }}
                sx={{ borderRadius: 2, fontWeight: 500, borderColor: '#0d3559', color: '#0d3559', '&:hover': { borderColor: '#0a2a45', backgroundColor: 'rgba(13,53,89,0.04)' } }}
              >
                Generate
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  if (formData.password) {
                    navigator.clipboard.writeText(formData.password);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }
                }}
                sx={{ borderRadius: 2, fontWeight: 500, borderColor: '#0d3559', color: '#0d3559', '&:hover': { borderColor: '#0a2a45', backgroundColor: 'rgba(13,53,89,0.04)' } }}
                disabled={!formData.password}
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </Box>

            <FormControl fullWidth variant="outlined" size="medium">
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
                label="Role"
              >
                {roles?.length > 0 ? (
                  roles
                    .filter(
                      (role) => role.name?.toLowerCase() !== "super admin"
                    )
                    .map((role) => (
                      <MenuItem key={role._id} value={role._id}>
                        {role.name}
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem disabled>No roles found</MenuItem>
                )}
              </Select>
            </FormControl>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <input
                  type="checkbox"
                  id="isVerified"
                  checked={formData.isVerified}
                  onChange={(e) =>
                    setFormData({ ...formData, isVerified: e.target.checked })
                  }
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Verified
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <input
                  type="checkbox"
                  id="isPublished"
                  checked={formData.isPublished}
                  onChange={(e) =>
                    setFormData({ ...formData, isPublished: e.target.checked })
                  }
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Published
                </Typography>
              </Box>
            </Box>

            {dialogType === "create" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <input
                  type="checkbox"
                  id="sendEmail"
                  checked={formData.sendEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, sendEmail: e.target.checked })
                  }
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Send account details to this email
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            sx={{ borderRadius: 2, fontWeight: 500, borderColor: '#0d3559', color: '#0d3559', '&:hover': { borderColor: '#0a2a45', backgroundColor: 'rgba(13,53,89,0.04)' } }}
          >
            Cancel
          </Button>
          { (userPermissions.canCreate || userPermissions.canEdit)  &&
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
              backgroundColor: "#0d3559",
              "&:hover": { backgroundColor: "#0a2a45" },
            }}
          >
            Save
          </Button>
          }
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default ManageUsers;
